import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { isLoggedIn as isLoggedInFn } from "../../hooks/useAuth";
import { ChevronDown } from "lucide-react";
import { useCountries, useCities, useCategories } from "../../hooks/useBusinessData";
import MiniDropdown from "../ui/MiniDropdown";
import {
  CATEGORY_BAR,
  CATEGORY_TOOLTIP,
} from "../../constants/categoryStyles";
import CategoryIcon from "../ui/CategoryIcon";
import { markIgnoreUntil } from "../../utils/clickGuard";
import { registerModalClose } from "../../utils/modalStack";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";

interface MainMenuProps {
  activeCategoryKey: string | null;
  activeCountryKey: string;
  activeCityKey: string | null;
  onCategoryChange: (key: string | null, id?: number | null) => void;
  onCountryChange: (key: string, id: number) => void;
  onCityChange: (key: string | null, id?: number | null) => void;
}

interface TooltipState {
  text: string;
  left: number;   // 화면 절대 좌표
  top: number;
}

export default function MainMenu({
  activeCategoryKey, activeCountryKey, activeCityKey,
  onCategoryChange, onCountryChange, onCityChange,
}: MainMenuProps) {
  const { t } = useTranslation();
  const dbCountries = useCountries();
  const dbCities = useCities(activeCountryKey);
  const dbCategories = useCategories();
  const cityOptions = [
    { key: "__all__", label: t("menu.allCities") },
    ...dbCities.map((c) => ({ key: c.key, label: c.label })),
  ];

  // --content-width 동적 측정 제거 (옛 코드는 카테고리 라벨 길이로 배너 폭이 변동 → 언어별 카드 크기 차이 발생).
  // 이제 index.css 의 고정 기본값 (1280px) 을 그대로 사용 — 모든 언어 동일 너비.

  // ── 모바일 카테고리 그리드 팝업 (요구사항 #254 #7) ──
  const [catGridOpen, setCatGridOpen] = useState(false);
  const catGridRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(catGridRef, catGridOpen);
  const [catBtnBottom, setCatBtnBottom] = useState(0);
  const catBtnRef = useRef<HTMLButtonElement>(null);
  const activeCatLabel = activeCategoryKey
    ? dbCategories.find((c) => c.key === activeCategoryKey)?.label || t("menu.allCategories")
    : t("menu.allCategories");
  // 외부 클릭 시 닫기 (그리드 위치는 버튼 onClick 에서 열기 전에 동기 측정 — 아래 참고)
  useEffect(() => {
    if (!catGridOpen) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (catBtnRef.current && !catBtnRef.current.contains(target)) {
        const grid = document.getElementById("cat-grid-popup");
        if (grid && !grid.contains(target)) setCatGridOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [catGridOpen]);

  // 요구사항 #550: 안드로이드 뒤로가기로 카테고리 팝업 닫기 — modalStack 등록(다른 팝업과 동일)
  useEffect(() => {
    if (!catGridOpen) return;
    const unregister = registerModalClose(() => setCatGridOpen(false));
    return () => unregister();
  }, [catGridOpen]);

  // ── hover 툴팁 (Portal 로 body 렌더, overflow 잘림 회피) ──
  const [tip] = useState<TooltipState | null>(null);
  // hover tooltip 은 현재 미사용 — onMouseEnter/onMouseLeave 연결이 빠진 상태.
  // 표시 layer 만 남아있어 tip 항상 null. 재활성화 시 setTip 호출 핸들러 추가 필요.

  return (
    // 데스크탑은 LeftSidebar 가 담당 → MainMenu 는 모바일 전용 (md:hidden)
    <div id="category-bar" className={`md:hidden ${CATEGORY_BAR.outer}`}>
      {/* w-fit로 자연 폭으로 줄어들어 mx-auto가 가운데 정렬 → 배너 영역과 동일한 좌단 위치 */}
      <div className={CATEGORY_BAR.inner}>
        {/* 국가 — 요구사항 #552: 필리핀 고정(선택 불가). 드롭다운 대신 고정 라벨. */}
        <div className="basis-1/3 min-w-0 md:basis-auto md:flex-none md:w-[88px] flex items-center gap-1 px-1.5 py-0.5 text-xs text-slate-900">
          {(() => {
            const ph = dbCountries.find((c) => c.key === "ph");
            return (<>
              <span className="shrink-0">{ph?.flag || "🇵🇭"}</span>
              <span className="truncate">{ph?.label || "Philippines"}</span>
            </>);
          })()}
        </div>

        {/* 도시 — 요구사항 #293: 아래 화살표 표시 */}
        <MiniDropdown
          theme="light"
          showChevron
          value={activeCityKey ?? "__all__"}
          options={cityOptions}
          onChange={(key) => {
            if (key === "__all__") { onCityChange(null, null); return; }
            const c = dbCities.find((x) => x.key === key);
            onCityChange(key, c?.id ?? null);
          }}
          className="basis-1/3 min-w-0 md:basis-auto md:flex-none md:w-[88px]"
        />

        {/* 카테고리: 모바일 — 요구사항 #254 #7: 클릭 시 아이콘 그리드 팝업 (드롭다운 X)
            요구사항 #293: 도시 MiniDropdown 트리거와 동일 스타일 적용 */}
        <div className="basis-1/3 min-w-0 md:hidden relative">
          <button
            ref={catBtnRef}
            onClick={() => {
              if (!isLoggedInFn()) { window.dispatchEvent(new Event("require-login")); return; }
              // #480: 팝업을 열기 전에 위치를 동기 측정 — useEffect 를 기다리면 첫 클릭 때
              // catBtnBottom 초기값(0)으로 화면 맨 위에 먼저 그려졌다가 실제 위치로
              // 다시 튀는 깜빡임이 있었음(재클릭부터는 이전 측정값이 남아있어 안 보였음).
              if (!catGridOpen && catBtnRef.current) {
                setCatBtnBottom(catBtnRef.current.getBoundingClientRect().bottom);
              }
              setCatGridOpen((o) => !o);
            }}
            className="flex w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-1.5 py-0.5 text-xs text-slate-900"
          >
            <span className="truncate">{activeCatLabel}</span>
            <ChevronDown className={`h-3 w-3 shrink-0 text-slate-500 transition-transform ${catGridOpen ? "rotate-180" : ""}`} />
          </button>
        </div>

      </div>

      {/* 카테고리 그리드 팝업 — Portal + fixed (viewport 전폭 보장)
          요구사항 #281-2 (재요청): 모바일 touch 흐름에서 backdrop click 이
          BannerCard 의 ghost click 으로 전파되어 배너 모달이 열림.
          모든 단계 (mousedown/touchstart/click) 에서 차단 + markIgnoreUntil
          로 후속 click 까지 무시. */}
      {catGridOpen && createPortal(
        <div
          className="fixed inset-0 z-[119]"
          onMouseDown={(e) => {
            e.stopPropagation();
            markIgnoreUntil(500);
            setCatGridOpen(false);
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            markIgnoreUntil(500);
            setCatGridOpen(false);
          }}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          aria-hidden
        />,
        document.body
      )}
      {catGridOpen && createPortal(
        <div
          ref={catGridRef}
          id="cat-grid-popup"
          className="fixed z-[120] rounded-lg border border-slate-300 bg-white shadow-xl"
          style={{ top: catBtnBottom + 4, left: "3.75px", right: "3.75px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="grid grid-cols-5 gap-x-0.5 gap-y-px p-1">
            {/* '전체 카테고리' 셀 제거 — 페이지 최초 로딩 시에만 적용되는 기본값. */}
            {dbCategories.map((cat) => {
              const active = activeCategoryKey === cat.key;
              return (
                <button
                  key={cat.key}
                  onClick={() => { onCategoryChange(cat.key, cat.id); setCatGridOpen(false); }}
                  className={`w-full flex flex-col items-center gap-1 rounded-lg p-1 transition-colors ${
                    active ? "text-red-600 font-bold" : "text-slate-900 hover:bg-slate-50"
                  }`}
                >
                  <CategoryIcon cat={cat} size="md" />
                  <span className="block w-full text-[12px] leading-none text-center break-keep">
                    {cat.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}

      {/* hover 툴팁 — Portal: body 직접 렌더, fixed 좌표, 부모 overflow 영향 없음 */}
      {tip && createPortal(
        <div
          role="tooltip"
          className={CATEGORY_TOOLTIP.box}
          style={{ left: tip.left, top: tip.top }}
        >
          {tip.text}
        </div>,
        document.body
      )}
    </div>
  );
}
